import { ApiTransport } from './ApiTransport';
import type {
  Activity,
  ActivityFilterOptions,
  ActivityFilters,
  CreateActivityInput,
  PaginatedResult,
} from './types';
import type {
  ActivityDeletionBatchView,
  DeletionItemOutcome,
} from '@internal/spassu-code-contract';
import {
  extractError,
  throwActivityReadError,
  deletionErrorMessage,
} from './apiErrors';
import { activityFilterQuery } from './activityFilters';

export class ActivitiesClient {
  constructor(private readonly transport: ApiTransport) {}

  async createActivity(input: CreateActivityInput): Promise<Activity> {
    const response = await this.transport.request(`/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(
        await extractError(response, 'Failed to create activity'),
      );
    }

    return response.json();
  }

  async listActivities(
    filters: ActivityFilters = {},
  ): Promise<PaginatedResult<Activity>> {
    const query = activityFilterQuery(filters, 'list');
    const response = await this.transport.request(`/activities${query}`);
    if (!response.ok) {
      throw new Error('Failed to list activities');
    }
    return response.json();
  }

  async getActivityFilterOptions(
    filters: Omit<ActivityFilters, 'createdBy' | 'page' | 'limit'> = {},
  ): Promise<ActivityFilterOptions> {
    const query = activityFilterQuery(filters, 'options');
    const response = await this.transport.request(
      `/activities/filter-options${query}`,
    );
    if (!response.ok) {
      throw new Error('Failed to get activity filter options');
    }
    return response.json();
  }

  async requestActivityDeletionBatch(
    activityIds: string[],
    idempotencyKey: string,
  ): Promise<ActivityDeletionBatchView> {
    const response = await this.transport.request(
      `/control-plane/admin/activity-deletion-batches`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ activityIds }),
      },
    );
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async getActivityDeletionBatch(
    batchId: string,
  ): Promise<ActivityDeletionBatchView | null> {
    const response = await this.transport.request(
      `/control-plane/admin/activity-deletion-batches/${encodeURIComponent(
        batchId,
      )}`,
      { cache: 'no-store' },
    );
    // A fully purged batch is removed, so 404 means "finished", not "error".
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async overrideActivityDeletionCleanup(
    activityId: string,
    idempotencyKey: string,
  ): Promise<{ activityId: string; outcome: DeletionItemOutcome }> {
    const response = await this.transport.request(
      `/control-plane/admin/activities/${encodeURIComponent(
        activityId,
      )}/deletion-override`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: '{}',
      },
    );
    if (!response.ok) {
      throw new Error(await deletionErrorMessage(response));
    }
    return response.json();
  }

  async getActivity(id: string): Promise<Activity> {
    const response = await this.transport.request(`/activities/${id}`);

    if (!response.ok) {
      await throwActivityReadError(response, 'Failed to get activity');
    }

    return response.json();
  }

  async finalizeActivity(activityId: string): Promise<Activity> {
    const response = await this.transport.request(
      `/activities/${encodeURIComponent(activityId)}/finalize`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' } },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to finalize activity');
    }
    return response.json();
  }
}
